import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { subProcessors } from "@/lib/sub-processors";

interface TrustPoint {
  title: string;
  body: string;
}

interface DataRow {
  data: string;
  handling: string;
}

interface DocLink {
  title: string;
  desc: string;
  href?: string;
  emailSubject?: string;
  actionLabel?: string;
}

interface DocGroup {
  title: string;
  docs: DocLink[];
}

const TRUST_POINTS: TrustPoint[] = [
  {
    title: "Kernverwerking in de EU",
    body:
      "De productiehosting, archiefopslag en het OpenAI-project voor ondersteunde klantinhoud zijn in Europese regio's ingericht. Uitzonderingen en doorgiften staan in het privacybeleid.",
  },
  {
    title: "Opnameretentie is begrensd",
    body:
      "De organisatie kiest voor gearchiveerde audio en gekoppelde opnamevelden een termijn van 30 tot 365 dagen. Andere gegevensstromen hebben een eigen termijn.",
  },
  {
    title: "Geen training door OpenAI",
    body:
      "Het productieproject is voor ondersteunde API-functies ingericht met EU-dataresidentie en Zero Data Retention. Carmen geeft klantinhoud niet vrij voor modeltraining.",
  },
  {
    title: "De arts valideert altijd",
    body:
      "Carmen maakt administratieve medische documentatie. De arts controleert en corrigeert de output vóór gebruik in het patiëntendossier.",
  },
];

const DATA_ROWS: DataRow[] = [
  {
    data: "Live audio en transcript",
    handling:
      "Verwerking voor dictatie en verslagopbouw. Live sessiestaat kan tijdens en na de workflow in onze database staan en valt niet automatisch onder de opname-retentie.",
  },
  {
    data: "Opnamegeschiedenis",
    handling:
      "Versleutelde audio-opslag en versleutelde tekst, beide bij Hetzner (EU). Organisatieretentie: 30-365 dagen, standaard 365.",
  },
  {
    data: "Account, rollen en organisatie",
    handling:
      "Beheer van authenticatie, gebruikersrollen, organisaties, afdelingen en toegangsrechten.",
  },
  {
    data: "Gebruiksmetadata",
    handling:
      "Metadata voor beveiliging, facturatie en betrouwbare werking. Foutcontext kan in bepaalde codepaden ook patiëntreferenties of inhoud bevatten; serverlogs worden 30 dagen bewaard.",
  },
];

const DOC_GROUPS: DocGroup[] = [
  {
    title: "Kernpakket voor zorgorganisaties",
    docs: [
      {
        title: "Verwerkingsovereenkomst (DPA)",
        desc: "Verwerkersafspraken conform artikel 28 AVG",
        emailSubject: "DPA",
      },
      {
        title: "Security assessment & TOMs",
        desc: "Technische en organisatorische maatregelen als input voor de beveiligingsbeoordeling van uw organisatie",
        emailSubject: "Security assessment en TOMs",
      },
      {
        title: "DPIA-ondersteuningspakket",
        desc: "Productinformatie, datastromen, risico's en maatregelen als input voor uw eigen DPIA",
        emailSubject: "DPIA-ondersteuningspakket",
      },
    ],
  },
  {
    title: "Publieke juridische informatie",
    docs: [
      {
        title: "Privacybeleid",
        desc: "Verwerking, rechtsgrond en bewaartermijnen",
        href: "/privacy",
      },
      {
        title: "Gebruiksvoorwaarden",
        desc: "Dienstvoorwaarden en verantwoordelijkheden",
        href: "/terms",
      },
    ],
  },
  {
    title: "Aanvullend en contact",
    docs: [
      {
        title: "Internationale doorgiften (TIA)",
        desc: "Doorgiftedocumentatie indien relevant voor de gekozen dienstverlening",
        emailSubject: "Internationale doorgiften en TIA",
      },
      {
        title: "Privacycontact",
        desc: "Privacyvragen en contact met de vrijwillig aangestelde DPO van Carmen",
        emailSubject: "Privacyvraag",
        actionLabel: "Contact",
      },
    ],
  },
];

const TEXT_LINK =
  "font-medium text-[var(--cs-navy)] underline decoration-[rgba(12,63,107,0.25)] underline-offset-4 hover:decoration-[rgba(12,63,107,0.55)]";

function mailto(subject: string) {
  return `mailto:info@askcarmen.eu?subject=${encodeURIComponent(`Toegang documenten - ${subject}`)}`;
}

function TrustSection({
  number,
  title,
  children,
  id,
}: {
  number: string;
  title: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="scroll-mt-28 border-t border-[#DCE4EA] py-9 sm:py-11">
      <div className="grid gap-4 sm:grid-cols-[44px_minmax(0,1fr)] sm:gap-5">
        <span className="text-[13px] font-medium tabular-nums text-[#8291A3]">{number}</span>
        <div className="min-w-0">
          <h2 className="text-[24px] font-semibold leading-tight tracking-[-0.02em] text-[var(--cs-navy)]">
            {title}
          </h2>
          <div className="mt-5 space-y-4 text-[15px] leading-7 text-[#536274]">{children}</div>
        </div>
      </div>
    </section>
  );
}

export function TrustPage() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const targetId = location.hash.slice(1);
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ block: "start" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  return (
    <PublicPageLayout>
      <article className="mx-auto w-full max-w-[900px] text-[var(--cs-navy)]">
        <header className="pb-10 sm:pb-14">
          <h1 className="text-[42px] font-semibold leading-[1.04] tracking-[-0.04em] sm:text-[58px]">
            Trust Center
          </h1>
          <p className="mt-4 text-[13px] text-[#6E7D8F]">
            <time dateTime="2026-08-22">22 augustus 2026</time>
          </p>
          <p className="mt-8 max-w-[800px] text-[17px] leading-8 text-[#536274]">
            Dit Trust Center beschrijft de technische en organisatorische waarborgen rond
            medische dictatie, toegangsbeheer, bewaring en leveranciers. Voor de juridische
            uitleg over persoonsgegevens verwijzen we naar het{" "}
            <Link to="/privacy" className={TEXT_LINK}>
              privacybeleid
            </Link>
            . Voor een DPA, security review, DPIA-ondersteuning of privacyvraag kunt u mailen
            naar{" "}
            <a href="mailto:info@askcarmen.eu" className={TEXT_LINK}>
              info@askcarmen.eu
            </a>
            .
          </p>
        </header>

        <TrustSection number="01" title="Wat u mag verwachten">
          <div className="grid min-w-0 gap-x-8 gap-y-7 sm:grid-cols-2">
            {TRUST_POINTS.map((point) => (
              <div key={point.title}>
                <h3 className="text-[16px] font-semibold leading-6 text-[var(--cs-navy)]">
                  {point.title}
                </h3>
                <p className="mt-2 text-[14px] leading-6 text-[#647286]">{point.body}</p>
              </div>
            ))}
          </div>
        </TrustSection>

        <TrustSection number="02" title="Operationele controls" id="security">
          <dl className="border-y border-[#DCE4EA]">
            <SecurityItem
              term="Encryptie"
              detail="Versleutelde verbindingen met TLS 1.2 of hoger. Gearchiveerde audio en de aan een opname gekoppelde transcripties en resultaten worden op applicatieniveau versleuteld opgeslagen met AES-256 (AES-256-GCM voor tekst, AES-256 SSE-C voor audio)."
            />
            <SecurityItem
              term="Toegang"
              detail="Passkey-authenticatie, gebruikersrollen en organisatiegegevens voor toegangsbeheer, met auditlogging voor gevoelige beheerhandelingen."
            />
            <SecurityItem
              term="Logging"
              detail="Operationele logs worden beperkt toegankelijk gehouden en 30 dagen bewaard. Fout- en diagnostiekcontext kan in sommige codepaden persoonsgegevens of klinische inhoud bevatten."
            />
            <SecurityItem
              term="Verwijdering"
              detail="Gearchiveerde opnames volgen de organisatie-instelling van 30–365 dagen. Live sessiestaat, lokale caches, taken, feedback en andere workflowgegevens volgen de afzonderlijke termijnen uit het privacybeleid."
            />
            <SecurityItem
              term="Beheer"
              detail="Carmen heeft gedocumenteerde processen voor informatiebeveiliging, incidenten, datalekken en kwetsbaarheden. Bij een datalek informeren we getroffen klanten conform de verwerkersovereenkomst uiterlijk 48 uur na vaststelling en melden we waar vereist binnen 72 uur aan de toezichthoudende autoriteit (artikel 33 en 34 AVG)."
            />
          </dl>
        </TrustSection>

        <TrustSection number="03" title="Verwerking en bewaring">
          <div className="border-y border-[#DCE4EA] sm:hidden">
            {DATA_ROWS.map((row) => (
              <div key={row.data} className="border-b border-[#E8EDF1] py-4 last:border-b-0">
                <h3 className="text-[14px] font-semibold leading-6 text-[var(--cs-navy)]">
                  {row.data}
                </h3>
                <p className="mt-1 text-[14px] leading-6 text-[#647286]">{row.handling}</p>
              </div>
            ))}
          </div>
          <div className="hidden min-w-0 overflow-x-auto border-y border-[#DCE4EA] sm:block">
            <table className="w-full min-w-[680px] border-collapse text-left">
              <thead>
                <tr className="border-b border-[#DCE4EA] text-[12px] uppercase tracking-[0.12em] text-[#738093]">
                  <th className="w-[220px] px-4 py-4 font-semibold">Gegeven</th>
                  <th className="px-4 py-4 font-semibold">Huidige verwerking</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8EDF1] text-[14px]">
                {DATA_ROWS.map((row) => (
                  <tr key={row.data}>
                    <td className="px-4 py-4 font-semibold text-[var(--cs-navy)]">{row.data}</td>
                    <td className="px-4 py-4 leading-6 text-[#647286]">{row.handling}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TrustSection>

        <TrustSection number="04" title="Documentatie voor uw beoordeling" id="documents">
          <div className="min-w-0 space-y-7">
            {DOC_GROUPS.map((group) => (
              <div key={group.title}>
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#738093]">
                  {group.title}
                </h3>
                <div className="border-y border-[#DCE4EA]">
                  {group.docs.map((doc) => (
                    <div
                      key={doc.title}
                      className="flex flex-col gap-3 border-b border-[#E8EDF1] py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="text-[15px] font-semibold text-[var(--cs-navy)]">
                          {doc.title}
                        </p>
                        <p className="mt-1 text-[14px] leading-6 text-[#647286]">{doc.desc}</p>
                      </div>
                      {doc.href ? (
                        <Link
                          to={doc.href}
                          className="shrink-0 text-[14px] font-semibold text-[var(--cs-rose)] hover:text-[var(--cs-rose-dark)]"
                        >
                          {doc.actionLabel ?? "Openen"}
                        </Link>
                      ) : (
                        <a
                          href={mailto(doc.emailSubject ?? doc.title)}
                          className="shrink-0 text-[14px] font-semibold text-[var(--cs-navy)] hover:text-[var(--cs-navy-strong)]"
                        >
                          {doc.actionLabel ?? "Aanvragen"}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p>
            Ons informatiebeveiligingsbeleid volgt de principes van ISO 27001:2022. Op
            aanvraag delen we de relevante documentatie voor uw security review, DPIA en
            eventuele internationale doorgiften.
          </p>
        </TrustSection>

        <TrustSection number="05" title="Actuele sub-verwerkers" id="sub-processors">
          <p>
            Dit zijn de dienstverleners die persoonsgegevens verwerken om Carmen te leveren.
            De locatie, gegevens en toepasselijke doorgiftewaarborg verschillen per
            leverancier en functie.
          </p>
          <p>
            Bij een wezenlijke wijziging in sub-verwerkers informeren we klanten minstens
            dertig dagen vooraf via e-mail. Klanten kunnen daarna binnen veertien dagen
            bezwaar maken volgens de verwerkersovereenkomst.
          </p>
          <div className="border-y border-[#DCE4EA] md:hidden">
            {subProcessors.map((processor) => (
              <div
                key={processor.name}
                className="flex gap-3 border-b border-[#E8EDF1] py-4 last:border-b-0"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4F9FC] text-[12px] font-semibold text-[var(--cs-navy)]">
                  {processor.logo ? (
                    <img src={processor.logo} alt="" className="h-5 w-5 object-contain" />
                  ) : (
                    processor.name.slice(0, 1)
                  )}
                </span>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-semibold text-[var(--cs-navy)]">
                    {processor.name}
                  </h3>
                  <p className="mt-1 text-[14px] leading-6 text-[#647286]">
                    {processor.category}
                  </p>
                  <p className="mt-2 text-[13px] leading-5 text-[#738093]">
                    {processor.data} · {processor.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden min-w-0 overflow-x-auto border-y border-[#DCE4EA] md:block">
            <table className="w-full min-w-[700px] table-fixed border-collapse text-left">
              <colgroup>
                <col className="w-[23%]" />
                <col className="w-[43%]" />
                <col className="w-[34%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-[#DCE4EA] text-[12px] uppercase tracking-[0.12em] text-[#738093]">
                  <th className="px-4 py-4 font-semibold">Verwerker</th>
                  <th className="px-4 py-4 font-semibold">Rol en gegevens</th>
                  <th className="px-4 py-4 font-semibold">Locatie / waarborg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8EDF1]">
                {subProcessors.map((processor) => (
                  <tr key={processor.name} className="text-[14px]">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4F9FC] text-[12px] font-semibold text-[var(--cs-navy)]">
                          {processor.logo ? (
                            <img src={processor.logo} alt="" className="h-5 w-5 object-contain" />
                          ) : (
                            processor.name.slice(0, 1)
                          )}
                        </span>
                        <span className="font-semibold text-[var(--cs-navy)]">
                          {processor.name}
                        </span>
                      </div>
                    </td>
                    <td className="break-words px-4 py-4">
                      <p className="font-semibold leading-5 text-[var(--cs-navy)]">
                        {processor.category}
                      </p>
                      <p className="mt-1.5 text-[13px] leading-5 text-[#738093]">
                        {processor.data}
                      </p>
                    </td>
                    <td className="break-words px-4 py-4 text-[#647286]">
                      {processor.location}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TrustSection>
      </article>
    </PublicPageLayout>
  );
}

function SecurityItem({ term, detail }: { term: string; detail: string }) {
  return (
    <div className="grid gap-1 border-b border-[#E8EDF1] py-4 last:border-b-0 sm:grid-cols-[170px_minmax(0,1fr)] sm:gap-6">
      <dt className="text-[14px] font-semibold leading-6 text-[var(--cs-navy)]">{term}</dt>
      <dd className="text-[14px] leading-6 text-[#647286]">{detail}</dd>
    </div>
  );
}
